import { useCopilotRuntimeOptional } from '@/components/agent-chat/copilot-runtime-provider.component';
import {
  renameChat,
  useChatTitles,
  withChatTitles,
} from '@/hooks/chat-titles.store';
import { isPendingKey } from '@/hooks/local-chats.util';
import { useAgentsAccess } from '@/hooks/use-agents-access.hook';
import { getApiBase } from '@/lib/api-base';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectAiChatOpen,
  setAiChatInitialMessage,
  setAiChatInputValue,
  setAiChatOpen,
} from '@/store/features/ui.feature';
import { fetchWebtokenHeadless } from '@/store/auth-helper';
import { useAuth, useUser } from '@clerk/react';
import {
  useMatches,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  activeChatTabId,
  chatTabTitle,
  chatTime,
  clearCutoff,
  mergeChatTabs,
  pruneClosedTabs,
  readClearedBefore,
  readClosedTabs,
  readReopenedTabs,
  reopenChatTabs,
  titleAgentTabs,
  visibleChatTabs,
  withLocalChats,
  writeClearedBefore,
  writeClosedTabs,
  writeReopenedTabs,
  type ChatTab,
} from './chat-tabs.util';
import { useChatLists } from './use-chat-lists.hook';

/**
 * What opening Clu asks for: a chat, a new one, a new one sent `text` as its
 * first message, or Clu with the chat it holds.
 */
type CluRequest =
  | { kind: 'open'; id: string }
  | { kind: 'new' }
  | { kind: 'ask'; text: string }
  | { kind: 'show' };

/**
 * A request waiting for /clu to render, and the trip there it rides on,
 * or null when made on /clu itself.
 */
interface PendingOpen {
  request: CluRequest;
  trip: string | null;
}

declare module '@tanstack/react-router' {
  interface HistoryState {
    /** The mark a trip of Clu's to /clu leaves (see useChatTabs). */
    cluTrip?: string;
  }
}

const newTrip = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

type CopilotRuntime = NonNullable<ReturnType<typeof useCopilotRuntimeOptional>>;

const NO_PINS: readonly string[] = [];

/** At most this many pinned chats are read one by one per organization. */
const PINNED_LOOKUP_LIMIT = 20;

/** A pinned chat whose read fails is tried this many times in all. */
const PINNED_LOOKUP_ATTEMPTS = 3;

/** The wait before a failed read is tried again, times the failures. */
export const PINNED_LOOKUP_RETRY_MS = 2_000;

/** The pinned chats read by id in one organization, and how they went. */
interface PinnedLookups {
  scope: string;
  /** Asked for, or answered: found or gone. These count toward the limit. */
  requested: Set<string>;
  /** Failed reads by chat, and when the next may go out. */
  failures: Map<string, { count: number; retryAt: number }>;
}

const newLookups = (scope: string): PinnedLookups => ({
  scope,
  requested: new Set(),
  failures: new Map(),
});

/**
 * One chat as a tab, read by its id for a pinned chat the recent lists leave
 * out: 'gone' when it no longer exists, null when the read failed.
 */
async function fetchChatTab(id: string): Promise<ChatTab | 'gone' | null> {
  try {
    const token = await fetchWebtokenHeadless();
    const response = await fetch(
      `${getApiBase()}/api/v1/agent/conversations/${encodeURIComponent(id)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (response.status === 404 || response.status === 400) return 'gone';
    if (!response.ok) return null;
    const chat = (await response.json()) as {
      id?: string;
      title?: string | null;
      modified_at?: string;
      context_agent_id?: string | null;
      renamed_at?: string | null;
    };
    if (chat.id !== id) return null;
    return {
      id,
      title: chatTabTitle(chat.title),
      modifiedAt: chat.modified_at ?? '',
      agentId: chat.context_agent_id ?? null,
      // The user named it, in this browser or another.
      ...(chat.renamed_at
        ? { renamed: true, renamed_at: chat.renamed_at }
        : {}),
    };
  } catch {
    return null;
  }
}

export interface ChatTabsState {
  /** Clu may be used here (not an AppSumo-only org). */
  available: boolean;
  tabs: ChatTab[];
  /** The pinned chats this user still has, by id. */
  pinned: ReadonlyMap<string, ChatTab>;
  /** The tab for the chat on screen; a chat just started is on screen too. */
  activeId: string | null;
  /** The chat on screen is in the Clu dock, beside another page. */
  activeInDock: boolean;
  /** Clu opens in the dock beside this page, as ⌘J opens it. */
  cluOpensInDock: boolean;
  /**
   * Every chat whose reply streams: the one on screen, and the Clu and
   * agent chats running beside it (CLE-567).
   */
  runningIds: ReadonlySet<string>;
  clear: () => void;
  /** Hides one chat from the tabs until it moves again. */
  close: (id: string) => void;
  /** Gives unpinned chats back their tabs, first, past Clear and Close. */
  reopen: (ids: readonly string[]) => void;
  /** Opens Clu with the chat it holds, where openCluChat opens a chat. */
  openClu: () => void;
  /**
   * Starts a new Clu chat with `text` as its first message, where
   * openCluChat opens one. A reply still streaming goes on beside it; when
   * no session can open, the question waits for that reply to end.
   */
  askClu: (text: string) => void;
  /**
   * Opens a Clu chat in the Clu dock beside the page, or on /clu where
   * the dock can't show it (see useChatTabs). A chat still waiting for its
   * id is the runtime's, shown as it is.
   */
  openCluChat: (id: string) => void;
  /**
   * Starts a new Clu chat where openCluChat opens one, as the dock's New
   * chat does.
   */
  newChat: () => void;
  /** Renames a chat, Clu's or an agent's: see renameChat. */
  rename: (id: string, title: string) => void;
}

/**
 * The user's chats as the sidebar's tabs, read as useChatLists reads them,
 * with the runtime's own on top: a chat shows the moment it is sent, and
 * moves first the moment the user sends in it. Pinned chats leave the tabs
 * for their pins; one the recent lists leave out is read by its id.
 */
export function useChatTabs(
  pinnedIds: readonly string[] = NO_PINS
): ChatTabsState {
  const runtime = useCopilotRuntimeOptional();
  const {
    available: canUseCopilot,
    cluChats,
    agentChats,
    agentNames,
    listsRead,
    local,
  } = useChatLists();
  const { user } = useUser();
  const userId = user?.id ?? null;
  const { orgId } = useAuth();
  const lookupScope = orgId ?? '';
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const aiChatOpen = useAppSelector(selectAiChatOpen);

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const chatParam = useRouterState({
    select: (s) => {
      const chat = (s.location.search as { chat?: unknown }).chat;
      return typeof chat === 'string' ? chat : undefined;
    },
  });
  // The rendered page, not the one loading: a chat opens once /clu is up
  // and an agent's workspace, which puts the general chat back as it leaves,
  // is gone.
  const onCopilotPage = useMatches({
    select: (matches) => matches.some((match) => match.routeId === '/clu'),
  });
  const onAgentRoute = useMatches({
    select: (matches) =>
      matches.some((match) => match.routeId === '/agents/$agentId'),
  });
  // An organization without agents gets the upgrade card on that route, not
  // a workspace: the dock stays the general Clu dock there.
  const agentsAccess = useAgentsAccess();
  const onAgentWorkspace =
    onAgentRoute && !(agentsAccess.isReady && !agentsAccess.unlocked);

  const streaming = runtime?.isStreaming ?? false;
  const conversationId = runtime?.conversationId ?? null;

  // Clear: a line per user, saved where storage allows and kept in memory
  // regardless, so it holds for the session even without storage.
  const storedClearedBefore = useMemo(
    () => readClearedBefore(userId),
    [userId]
  );
  const [sessionCleared, setSessionCleared] = useState<Record<string, number>>(
    {}
  );
  const sessionValue = userId ? sessionCleared[userId] : undefined;
  const clearedBefore =
    sessionValue !== undefined
      ? Math.max(sessionValue, storedClearedBefore ?? sessionValue)
      : storedClearedBefore;

  // A chat started here and renamed before a list holds it takes its title
  // too.
  const titles = useChatTitles();
  const allChats = useMemo(
    () =>
      canUseCopilot
        ? titleAgentTabs(
            withChatTitles(
              withLocalChats(mergeChatTabs(cluChats, agentChats), local),
              titles
            ),
            agentNames
          )
        : [],
    [agentChats, agentNames, canUseCopilot, cluChats, local, titles]
  );
  // Closed chats, and chats unpinned back into the tabs: per user beside
  // the Clear line, and kept in memory too. Once changed in this session,
  // the copy in memory is the whole list.
  const storedClosed = useMemo(() => readClosedTabs(userId), [userId]);
  const [sessionClosed, setSessionClosed] = useState<
    Record<string, Record<string, number>>
  >({});
  const closed = (userId ? sessionClosed[userId] : undefined) ?? storedClosed;
  const storedReopened = useMemo(() => readReopenedTabs(userId), [userId]);
  const [sessionReopened, setSessionReopened] = useState<
    Record<string, Record<string, number>>
  >({});
  const reopened =
    (userId ? sessionReopened[userId] : undefined) ?? storedReopened;
  const saveTabState = useCallback(
    (next: {
      closed?: Record<string, number>;
      reopened?: Record<string, number>;
    }) => {
      if (!userId) return;
      if (next.closed) {
        const value = next.closed;
        writeClosedTabs(userId, value);
        setSessionClosed((prev) => ({ ...prev, [userId]: value }));
      }
      if (next.reopened) {
        const value = next.reopened;
        writeReopenedTabs(userId, value);
        setSessionReopened((prev) => ({ ...prev, [userId]: value }));
      }
    },
    [userId]
  );

  // Pinned chats the recent lists leave out, read by id once those lists
  // are back: a chat found, or 'gone'. Kept per organization, since the
  // pins are; a failed read is tried again a few times.
  const [lookedUp, setLookedUp] = useState<{
    scope: string;
    found: Record<string, ChatTab | 'gone'>;
  }>({ scope: lookupScope, found: {} });
  const lookedUpHere = useMemo(
    () => (lookedUp.scope === lookupScope ? lookedUp.found : {}),
    [lookedUp, lookupScope]
  );
  const lookups = useRef(newLookups(lookupScope));
  const [retries, setRetries] = useState(0);
  const retryTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(retryTimer.current), []);
  useEffect(() => {
    if (!canUseCopilot || !listsRead) return;
    if (lookups.current.scope !== lookupScope)
      lookups.current = newLookups(lookupScope);
    const here = lookups.current;
    const known = new Set(allChats.map((chat) => chat.id));
    const now = Date.now();
    const unread = pinnedIds.filter(
      (id) =>
        !isPendingKey(id) &&
        !known.has(id) &&
        !here.requested.has(id) &&
        (here.failures.get(id)?.count ?? 0) < PINNED_LOOKUP_ATTEMPTS
    );
    const retryAt = (id: string) => here.failures.get(id)?.retryAt ?? 0;
    // A failed read goes out again once its wait is over.
    const next = Math.min(...unread.map(retryAt).filter((time) => time > now));
    window.clearTimeout(retryTimer.current);
    if (Number.isFinite(next))
      retryTimer.current = window.setTimeout(
        () => setRetries((count) => count + 1),
        next - now
      );
    const missing = unread
      .filter((id) => retryAt(id) <= now)
      .slice(0, Math.max(0, PINNED_LOOKUP_LIMIT - here.requested.size));
    if (missing.length === 0) return;
    for (const id of missing) here.requested.add(id);
    void Promise.all(
      missing.map(async (id) => [id, await fetchChatTab(id)] as const)
    ).then((results) => {
      // The organization changed while the reads were out.
      if (lookups.current !== here) return;
      for (const [id, result] of results) {
        if (result) continue;
        here.requested.delete(id);
        const count = (here.failures.get(id)?.count ?? 0) + 1;
        here.failures.set(id, {
          count,
          retryAt: Date.now() + PINNED_LOOKUP_RETRY_MS * count,
        });
      }
      // A new object even when every read failed, so the effect runs again
      // and waits for the retry.
      setLookedUp((prev) => {
        const found = prev.scope === here.scope ? { ...prev.found } : {};
        for (const [id, result] of results) if (result) found[id] = result;
        return { scope: here.scope, found };
      });
    });
  }, [allChats, canUseCopilot, listsRead, lookupScope, pinnedIds, retries]);

  // A pinned or unpinned chat read by its id takes the titles given too.
  const pinned = useMemo(() => {
    const byId = new Map(allChats.map((chat) => [chat.id, chat]));
    const found = new Map<string, ChatTab>();
    if (!canUseCopilot) return found;
    const extra: ChatTab[] = [];
    for (const id of pinnedIds) {
      const chat = byId.get(id);
      if (chat) found.set(id, chat);
      else {
        const read = lookedUpHere[id];
        if (read && read !== 'gone') extra.push(read);
      }
    }
    for (const chat of titleAgentTabs(
      withChatTitles(extra, titles),
      agentNames
    ))
      found.set(chat.id, chat);
    return found;
  }, [agentNames, allChats, canUseCopilot, lookedUpHere, pinnedIds, titles]);

  // An unpinned chat the recent lists leave out was read by its id; it
  // still has its tab.
  const tabChats = useMemo(() => {
    const listed = new Set(allChats.map((chat) => chat.id));
    const extra = Object.keys(reopened).flatMap((id) => {
      const read = listed.has(id) ? undefined : lookedUpHere[id];
      return read && read !== 'gone' ? [read] : [];
    });
    return extra.length > 0
      ? [
          ...allChats,
          ...titleAgentTabs(withChatTitles(extra, titles), agentNames),
        ]
      : allChats;
  }, [agentNames, allChats, lookedUpHere, reopened, titles]);
  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);
  const tabs = useMemo(
    () =>
      visibleChatTabs(tabChats, {
        clearedBefore,
        closed,
        pinned: pinnedSet,
        reopened,
      }),
    [clearedBefore, closed, pinnedSet, reopened, tabChats]
  );

  const clear = useCallback(() => {
    const cutoff = clearCutoff(tabs);
    if (!userId || cutoff === null) return;
    writeClearedBefore(userId, cutoff);
    setSessionCleared((prev) => ({ ...prev, [userId]: cutoff }));
    // Clear hides every tab listed now, unpinned ones included.
    saveTabState({ reopened: {} });
  }, [saveTabState, tabs, userId]);

  const close = useCallback(
    (id: string) => {
      const tab = tabs.find((candidate) => candidate.id === id);
      if (!userId || !tab) return;
      saveTabState({
        closed: pruneClosedTabs(
          { ...closed, [id]: chatTime(tab.modifiedAt) },
          clearedBefore
        ),
        reopened: Object.fromEntries(
          Object.entries(reopened).filter(([other]) => other !== id)
        ),
      });
    },
    [clearedBefore, closed, reopened, saveTabState, tabs, userId]
  );

  const reopen = useCallback(
    (ids: readonly string[]) => {
      if (!userId || ids.length === 0) return;
      // First among the tabs, whatever the server's clock says of the rest.
      const newest = Math.max(
        0,
        ...tabChats.map((chat) => chatTime(chat.modifiedAt))
      );
      saveTabState(
        reopenChatTabs(ids, Math.max(Date.now(), newest + 1), {
          reopened,
          closed,
        })
      );
    },
    [closed, reopened, saveTabState, tabChats, userId]
  );

  // The dock shows Clu's own chat everywhere but /clu, home and an
  // agent's workspace (where it holds the agent's chats).
  const onHome = pathname === '/';
  const cluBesidePage = !onCopilotPage && !onHome && !onAgentWorkspace;
  const dockShowsClu = aiChatOpen && cluBesidePage;
  // Clu opens in the dock beside the page wherever the dock may show it, on
  // the rules ⌘J and the dock keep: once onboarding is done, where Clu may be
  // used. Elsewhere it opens on /clu.
  const hasCompletedOnboarding = !!user?.unsafeMetadata?.completed_onboarding;
  const opensInDock = cluBesidePage && canUseCopilot && hasCompletedOnboarding;
  // A chat started here is the runtime's: under its key until it has an id,
  // and under that id before the workspace's URL names it.
  const started = local.pending?.orphaned ? null : local.pending;
  const pendingId =
    started && (started.id === null || started.id === conversationId)
      ? (started.id ?? started.key)
      : null;
  const activeId = activeChatTabId({
    tabs: [...tabs, ...pinned.values()],
    pathname,
    chatParam,
    runtimeConversationId: conversationId,
    cluOnScreen: onCopilotPage || dockShowsClu,
    pendingId,
  });
  // The chat on screen runs under its key until it has an id.
  const runningOnScreen =
    activeId && streaming && activeId === (conversationId ?? pendingId)
      ? activeId
      : null;
  const runningChatIds = runtime?.runningChatIds;
  const runningIds = useMemo(() => {
    const ids = new Set(runningChatIds ?? []);
    if (runningOnScreen) ids.add(runningOnScreen);
    return ids;
  }, [runningChatIds, runningOnScreen]);

  // A fresh chat for a question, cleared first as the home's chatbox does,
  // with an empty composer, as New chat leaves it.
  const startAsk = useCallback(
    (text: string, current: CopilotRuntime) => {
      if (current.messages.length > 0) current.clearChat();
      dispatch(setAiChatInputValue(undefined));
      dispatch(setAiChatInitialMessage(text));
    },
    [dispatch]
  );

  // A question asked while a reply streams, with no session to open beside
  // it, waits here for it to end, since a reply is never cut off, then goes
  // once. Not in the dock's seed, which closing the dock (Expand, Close, ⌘J)
  // drops.
  const [waitingAsk, setWaitingAsk] = useState<string | null>(null);

  // The chat the request names, in the runtime the dock and /clu share, as
  // the Chats rail and the dock's New chat do. A chat already open in a
  // session of its own is switched to, live; one running keeps running while
  // another opens beside it (CLE-567).
  const applyCluRequest = useCallback(
    (next: CluRequest, current: CopilotRuntime) => {
      if (next.kind === 'open') {
        // A chat waiting for its id is the runtime's own: nothing to load.
        if (!isPendingKey(next.id) && current.conversationId !== next.id)
          current.openConversation(next.id);
      } else if (next.kind === 'new') {
        current.startNewChat();
        dispatch(setAiChatInputValue(undefined));
      } else if (next.kind === 'ask') {
        if (current.messages.length > 0 && current.newSession()) {
          // A session of its own, sent at once: nothing is cleared or cut.
          setWaitingAsk(null);
          dispatch(setAiChatInputValue(undefined));
          dispatch(setAiChatInitialMessage(next.text));
        } else if (current.isStreaming) {
          setWaitingAsk(next.text);
        } else {
          setWaitingAsk(null);
          startAsk(next.text, current);
        }
      }
    },
    [dispatch, startAsk]
  );

  const runtimeRef = useRef(runtime);
  runtimeRef.current = runtime;
  useEffect(() => {
    const current = runtimeRef.current;
    if (waitingAsk === null || streaming || !current) return;
    setWaitingAsk(null);
    startAsk(waitingAsk, current);
  }, [startAsk, streaming, waitingAsk]);

  // Opening a Clu chat, or starting one, on /clu waits for it to render,
  // and holds only for the trip there it made: /clu reached another way,
  // as after a page that blocks leaving it kept the user, drops it.
  const [pending, setPending] = useState<PendingOpen | null>(null);
  const arrivedTrip = useRouterState({
    select: (s) => s.location.state?.cluTrip ?? null,
  });
  useEffect(() => {
    const current = runtimeRef.current;
    if (!pending || !onCopilotPage || !current) return;
    if (pending.trip === null || pending.trip === arrivedTrip)
      applyCluRequest(pending.request, current);
    setPending(null);
  }, [applyCluRequest, arrivedTrip, onCopilotPage, pending]);

  // Beside the page, the dock opens with the chat at once, and the page
  // stays. Elsewhere, /clu.
  const showClu = useCallback(
    (next: CluRequest) => {
      const current = runtimeRef.current;
      if (opensInDock && current) {
        setPending(null);
        dispatch(setAiChatOpen(true));
        applyCluRequest(next, current);
        return;
      }
      if (onCopilotPage) {
        setPending({ request: next, trip: null });
        return;
      }
      const trip = newTrip();
      setPending({ request: next, trip });
      void navigate({ to: '/clu', state: { cluTrip: trip } });
    },
    [applyCluRequest, dispatch, navigate, onCopilotPage, opensInDock]
  );
  const openCluChat = useCallback(
    (id: string) => showClu({ kind: 'open', id }),
    [showClu]
  );
  const newChat = useCallback(() => showClu({ kind: 'new' }), [showClu]);
  const openClu = useCallback(() => showClu({ kind: 'show' }), [showClu]);
  const askClu = useCallback(
    (text: string) => showClu({ kind: 'ask', text }),
    [showClu]
  );

  return {
    available: canUseCopilot,
    tabs,
    pinned,
    activeId,
    activeInDock: activeId !== null && dockShowsClu,
    cluOpensInDock: opensInDock,
    runningIds,
    clear,
    close,
    reopen,
    openClu,
    askClu,
    openCluChat,
    newChat,
    rename: renameChatInPlace,
  };
}

/** Renames a chat from the sidebar; the store says when it fails. */
function renameChatInPlace(id: string, title: string): void {
  void renameChat(id, title);
}
